import React from 'react';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  HIDE_CART_COUNT_KEY,
  DATA_ATTR_COUNT_HIDE_RULE,
  CART_COUNT_HIDE_RULES,
  CART_STATES,
} from '@packages/systems/commerce/constants';
import {withSystem, Plugins, Atom} from '@packages/systems/wfdl';
import {createRgbaColor} from '@packages/systems/core/utils/ColorUtils';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {get, setIn} from '@packages/systems/core/utils/ImmUtils';
import {equals} from '@packages/utilities/fp/utils';
import {
  TRecord,
  TTypeConstructor,
  TText,
  TBoolean,
} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment} from '@packages/systems/render';
import {
  EvaluatedNodeData,
  getBoolean,
  getInPojo,
} from '@packages/utilities/data-utils';

const alwaysHideCartCount = get(HIDE_CART_COUNT_KEY);
const hideCountWhenEmptyCart = (data: EvaluatedNodeData) =>
  getBoolean(getInPojo(data, ['commerce', 'hideCartWhenEmpty']));
const setDisplayNone = setIn(['style', 'display'], 'none');
const EMPTY_CART_COUNT = '0';
const isEmptyCart = equals(EMPTY_CART_COUNT);

const CommerceCartOpenLinkCount = Atom.createRender((props) => {
  const {attributes, classes, context, data, generateAttributes, innerHTML} =
    props;
  const runtimeEnvironment = useRuntimeEnvironment();
  // Count displayed is determined both by runtime, cart state, and state of user's commerceOrder
  // Use previewItems count if in Designer
  const isEmptyState = context.get('tempState') === CART_STATES.EMPTY;
  const isInDesigner = runtimeEnvironment === WebflowEnvironments.DESIGNER;
  const previewCount = isEmptyState
    ? EMPTY_CART_COUNT
    : context.get('previewItemsCount');
  // Set to 0 if commerceOrder preview items is null
  const count = innerHTML || EMPTY_CART_COUNT;

  const cartIsEmpty = isInDesigner
    ? isEmptyCart(previewCount)
    : // @ts-expect-error inner HTML type is wack
      isEmptyCart(count);

  const styledAttributes =
    (hideCountWhenEmptyCart(data) && cartIsEmpty) ||
    alwaysHideCartCount(context)
      ? setDisplayNone(attributes)
      : attributes;
  let attributesWithData = styledAttributes;
  if (alwaysHideCartCount(context)) {
    attributesWithData = styledAttributes.set(
      DATA_ATTR_COUNT_HIDE_RULE,
      CART_COUNT_HIDE_RULES.ALWAYS
    );
  } else if (hideCountWhenEmptyCart(data)) {
    attributesWithData = styledAttributes.set(
      DATA_ATTR_COUNT_HIDE_RULE,
      CART_COUNT_HIDE_RULES.EMPTY
    );
  }

  return (
    <div {...generateAttributes(attributesWithData, classes)}>
      {isInDesigner
        ? previewCount
        : // TODO (platform): fix innerHTML type issues

          count}
    </div>
  );
});

const config = createIntegratedAtom({
  displayName: 'Cart Quantity',
  showEmpty: false,
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Basic'), iconName('Text')),
  },
  style: {
    base: {
      main: {
        noPseudo: {
          display: 'inline-block',
          height: px(18),
          minWidth: px(18),
          marginLeft: px(8),
          paddingRight: px(6),
          paddingLeft: px(6),
          borderBottomLeftRadius: px(9),
          borderBottomRightRadius: px(9),
          borderTopLeftRadius: px(9),
          borderTopRightRadius: px(9),
          backgroundColor: createRgbaColor('fff'),
          color: createRgbaColor('3898ec'),
          fontSize: px(11),
          lineHeight: px(18),
          fontWeight: 700,
          textAlign: 'center',
        },
      },
    },
  },
  constraints: {
    ancestors: [{is: 'CommerceCartOpenLink', rule: 'ExactlyOne'}],
  },
  render: CommerceCartOpenLinkCount,
  fields: {
    text: withSystem({private: true}, TBoolean),
    commerce: TRecord({
      type: withSystem({private: true}, TText),
      hideCartWhenEmpty: withSystem(
        {
          labelVariant: 'hidden',
          label: 'Hide when cart is empty',
        },
        TTypeConstructor(
          qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
        )
      ),
    }),
  },
});

export default config;
